// src/hooks/useTransactionDetail.ts
// Hook for fetching single transaction details
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TransactionDetail {
  success: boolean;
  transaction: {
    id: string;
    user_id: string;
    kind: 'drain' | 'va_deposit' | 'payout' | 'transfer' | 'return';
    status: string;
    amount_in: number | null;
    asset_in: string | null;
    amount_out: number | null;
    asset_out: string | null;
    fee: number;
    bridge_ids: Record<string, unknown>;
    liquidation_address_id: string | null;
    virtual_account_id: string | null;
    payout_route_id: string | null;
    external_account_id: string | null;
    created_at: string;
    updated_at: string;
    settled_at: string | null;
    metadata: Record<string, unknown>;
  };
  liquidation_address?: {
    id: string;
    address: string;
    chain: string;              // ✅ Correct (matches DB schema)
    asset: string;
    currency: string;
    external_id: string;        // ✅ Correct (matches DB schema, was bridge_id)
    status: string;
  } | null;
  virtual_account?: {
    id: string;
    source_currency: string;
    bridge_va_id: string;
    status: string;
  } | null;
  external_account?: {
    id: string;
    account_type: string;
    last_4: string;
    bank_name: string;
    currency: string;
    external_account_id: string;
  } | null;
  payout_route?: {
    id: string;
    currency: string;
    rail: string;
    is_default: boolean;
    status: string;
  } | null;
}

export function useTransactionDetail(txId: string | null) {
  return useQuery({
    queryKey: ['transaction_detail', txId],
    queryFn: async () => {
      if (!txId) return null;

      const { data, error } = await supabase.rpc('user_transaction_detail', {
        p_tx_id: txId,
      });

      if (error) {
        throw new Error(error.message || 'Failed to load transaction details');
      }

      return data as TransactionDetail | null;
    },
    enabled: !!txId,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error: any) => {
      // Don't retry on 401/403/404
      if (error?.code && ['401', '403', '404', 'PGRST001'].includes(error.code)) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

